/**
 * Enhanced Zoho Sign Integration Service
 *
 * Provides robust, production-ready Zoho Sign integration with comprehensive
 * error handling, request queuing, and real-time status monitoring.
 *
 * Features:
 * - Request queuing for multiple concurrent contracts
 * - Real-time health monitoring with detailed metrics
 * - Automatic retry with exponential backoff
 * - Progress tracking for multi-contract operations
 * - Request cancellation support
 * - Comprehensive error handling and recovery
 * - Rate limiting and request throttling
 * - Connection status monitoring
 *
 * Usage:
 * import zohoService from '@/service/zohoService'
 * const service = zohoService()
 * await service.sendMultipleContracts(contracts, progressCallback)
 */

import axios from 'axios'
import moment from 'moment'
import apiErrorHandler from '@/helper/apiErrorHandler'
import zohoConfig from './zohoConfig'

export default function zohoService() {
  const { retryWithBackoff, handleApiError, createCancellationToken, batchRequests } = apiErrorHandler()

  // Service state
  const state = {
    isHealthy: null,
    lastHealthCheck: null,
    responseTime: null,
    errorCount: 0,
    successCount: 0,
    activeRequests: 0,
    queue: [],
    healthCheckTimer: null,
    connectionStatus: {
      isOnline: null,
      lastChecked: null,
      responseTime: null,
      error: null,
      consecutiveFailures: 0
    }
  }

  // Configuration
  const config = {
    healthCheckInterval: 30000, // 30 seconds
    maxRetries: 3,
    requestTimeout: 15000, // 15 seconds
    maxConcurrentRequests: 3,
    rateLimitDelay: 1000 // 1 second between batched requests
  }

  /**
   * Perform comprehensive health check of Zoho middleware
   */
  async function performHealthCheck(detailed = false) {
    const startTime = performance.now()

    try {
      const healthEndpoint = zohoConfig.healthEndpoint()

      const response = await axios.get(healthEndpoint, {
        timeout: config.requestTimeout,
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      })

      const responseTime = Math.round(performance.now() - startTime)

      // Validate response structure
      const isValidResponse = response.status === 200
                             && response.data
                             && response.data.status === 'OK'

      if (!isValidResponse) {
        throw new Error('Invalid health check response')
      }

      // Update state
      state.isHealthy = true
      state.lastHealthCheck = new Date()
      state.responseTime = responseTime
      state.connectionStatus = {
        isOnline: true,
        lastChecked: new Date(),
        responseTime,
        error: null,
        consecutiveFailures: 0
      }

      // Perform additional checks if detailed health check requested
      let additionalInfo = {}
      if (detailed) {
        try {
          const debugEndpoint = zohoConfig.debugConnectivityEndpoint()
          const debugResponse = await axios.get(debugEndpoint, {
            timeout: config.requestTimeout
          })
          additionalInfo = debugResponse.data || {}
        } catch (debugError) {
          additionalInfo = { debugError: debugError.message }
        }
      }

      return {
        isHealthy: true,
        status: 'OK',
        responseTime,
        timestamp: new Date(),
        details: response.data,
        additionalInfo,
        environment: zohoConfig.getEnvironmentName()
      }
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime)
      const errorInfo = handleApiError(error, 'Zoho Health Check')

      // Update state
      state.isHealthy = false
      state.lastHealthCheck = new Date()
      state.responseTime = null
      state.errorCount += 1
      state.connectionStatus = {
        isOnline: false,
        lastChecked: new Date(),
        responseTime: null,
        error: errorInfo.friendlyMessage.message,
        consecutiveFailures: state.connectionStatus.consecutiveFailures + 1
      }

      return {
        isHealthy: false,
        status: 'ERROR',
        responseTime,
        timestamp: new Date(),
        error: errorInfo,
        environment: zohoConfig.getEnvironmentName()
      }
    }
  }

  /**
   * Start automated health monitoring
   */
  function startHealthMonitoring() {
    if (state.healthCheckTimer) {
      clearInterval(state.healthCheckTimer)
    }

    // Initial health check
    performHealthCheck()

    // Set up periodic checks
    state.healthCheckTimer = setInterval(() => {
      performHealthCheck()
    }, config.healthCheckInterval)
  }

  /**
   * Stop automated health monitoring
   */
  function stopHealthMonitoring() {
    if (state.healthCheckTimer) {
      clearInterval(state.healthCheckTimer)
      state.healthCheckTimer = null
    }
  }

  /**
   * Get current connection status
   */
  function getConnectionStatus() {
    return {
      ...state.connectionStatus,
      metrics: {
        errorCount: state.errorCount,
        successCount: state.successCount,
        activeRequests: state.activeRequests,
        uptime: state.connectionStatus.consecutiveFailures === 0 ? 'Good' : 'Poor'
      }
    }
  }

  /**
   * Create contract payload from storage and offer data
   */
  function createContractPayload(storage, offer, index, total) {
    // Validate required fields
    if (!offer.email || offer.email.trim() === '') {
      throw new Error(`Ungültige E-Mail-Adresse für Vertrag ${index + 1}. Eine gültige E-Mail-Adresse ist erforderlich.`)
    }

    // The final price (after discount) already includes VAT
    const grossPrice = storage.finalPrice || storage.price

    return {
      requests: [
        {
          recipient_name: `${offer.firstName || ''} ${offer.lastName || ''}`.trim(),
          recipient_email: offer.email.trim(),
          notes: `Vertrag für Lager ${storage.storageIdent} (${index + 1} von ${total})`,
          field_data: {
            field_text_data: {
              phone: offer.mobileNumber || offer.phoneNumber || 'Nicht angegeben',
              'e-mail': offer.email.trim(),
              adress: `${offer.street || ''} ${offer.streetNumber || ''}`.trim(),
              name: `${offer.firstName || ''} ${offer.lastName || ''}`.trim(),
              containerID: `ID: ${storage.storageIdent}`,
              containerName: `Bezeichnung: ${storage.name}`,
              containerSize: `Größe: ${storage.storageSpace} m²`,
              containerLocation: storage.locationAddress || 'Adresse nicht verfügbar',
              containerPrice: `Preis: ${storage.price.toFixed(2)} €`,
              containerDisc: `Rabatt: ${(storage.discount || 0).toFixed(2)} €`,
              containerSum: `Summe mtl.: ${grossPrice.toFixed(2)} €`,
              endDate: storage.rentEnd ? moment(storage.rentEnd).format('DD.MM.YYYY') : 'auf unbestimmte Zeit',
              startDate: storage.rentStart ? moment(storage.rentStart).format('DD.MM.YYYY') : moment().format('DD.MM.YYYY'),
              sumPrice: `${grossPrice.toFixed(2)} €`,
              calcMWST: `${(grossPrice - (grossPrice / 1.19)).toFixed(2)} €`,
              rentPrice: `${(grossPrice / 1.19).toFixed(2)} €`
            },
            field_boolean_data: {},
            field_date_data: {},
            field_radio_data: {},
            field_checkboxgroup_data: {}
          },
          action_id: '74430000000122070',
          signing_order: 1,
          role: 'Kunde',
          verify_recipient: false,
          private_notes: `Vertrag ${index + 1} von ${total}`
        }
      ]
    }
  }

  /**
   * Send single contract to Zoho Sign
   */
  async function sendSingleContract(payload, context = 'Contract', cancelToken = null) {
    state.activeRequests += 1

    try {
      const response = await retryWithBackoff(
        async () => {
          if (cancelToken?.isCancelled) {
            throw new Error('Request cancelled')
          }

          const sendEndpoint = zohoConfig.sendTemplateEndpoint()
          return axios.post(sendEndpoint, payload, {
            timeout: config.requestTimeout,
            headers: {
              'Content-Type': 'application/json'
            }
          })
        },
        {
          context,
          customRetryConfig: {
            maxRetries: config.maxRetries,
            initialDelay: 1000,
            maxDelay: 8000
          }
        },
        null,
        cancelToken
      )

      state.successCount += 1
      return {
        success: true,
        data: response.data,
        timestamp: new Date()
      }
    } catch (error) {
      state.errorCount += 1
      throw error
    } finally {
      state.activeRequests -= 1
    }
  }

  /**
   * Send multiple contracts with progress tracking and error handling
   */
  async function sendMultipleContracts(
    storages,
    offer,
    progressCallback = null,
    cancelToken = null
  ) {
    if (!storages || storages.length === 0) {
      throw new Error('No storages provided for contract generation')
    }

    // Pre-flight health check
    const healthCheck = await performHealthCheck(true)
    if (!healthCheck.isHealthy) {
      throw new Error(`Zoho service unavailable: ${healthCheck.error?.friendlyMessage?.message}`)
    }

    if (progressCallback) {
      progressCallback({
        type: 'start',
        total: storages.length,
        message: 'Starte Vertragsversendung...',
        healthStatus: healthCheck
      })
    }

    // Create contract requests
    const contractRequests = storages.map((storage, index) => ({
      apiCall: () => sendSingleContract(
        createContractPayload(storage, offer, index, storages.length),
        `Vertrag ${index + 1}/${storages.length}`,
        cancelToken
      ),
      context: `Vertrag ${index + 1} (${storage.storageIdent})`,
      storage,
      index
    }))

    // Execute batch requests with progress tracking
    const results = await batchRequests(
      contractRequests,
      {
        maxConcurrency: config.maxConcurrentRequests,
        continueOnError: false, // Stop on first error for contract sending
        context: 'Vertragsversendung'
      },
      progress => {
        if (progressCallback) {
          progressCallback({
            ...progress,
            phase: 'sending'
          })
        }
      },
      cancelToken
    )

    // Process results
    if (results.hasErrors) {
      const firstError = results.errors.find(e => e !== undefined)
      throw new Error(`Contract sending failed: ${firstError?.friendlyMessage?.message}`)
    }

    if (progressCallback) {
      progressCallback({
        type: 'complete',
        total: storages.length,
        successful: results.successCount,
        message: `Alle ${storages.length} Verträge erfolgreich versendet`
      })
    }

    return {
      success: true,
      contractsSent: results.successCount,
      total: storages.length,
      results: results.results,
      timestamp: new Date()
    }
  }

  /**
   * Test Zoho connectivity with comprehensive diagnostics
   */
  async function testConnectivity() {
    const diagnostics = {
      timestamp: new Date(),
      environment: zohoConfig.getEnvironmentName(),
      baseUrl: zohoConfig.getZohoMiddlewareUrl(),
      tests: {}
    }

    // Test 1: Basic health check
    try {
      const healthResult = await performHealthCheck(false)
      diagnostics.tests.healthCheck = {
        passed: healthResult.isHealthy,
        responseTime: healthResult.responseTime,
        details: healthResult.details || healthResult.error
      }
    } catch (error) {
      diagnostics.tests.healthCheck = {
        passed: false,
        error: error.message
      }
    }

    // Test 2: Debug endpoint
    try {
      const debugEndpoint = zohoConfig.debugConnectivityEndpoint()
      const debugResponse = await axios.get(debugEndpoint, { timeout: 5000 })
      diagnostics.tests.debugEndpoint = {
        passed: debugResponse.status === 200,
        responseTime: debugResponse.data?.responseTime,
        details: debugResponse.data
      }
    } catch (error) {
      diagnostics.tests.debugEndpoint = {
        passed: false,
        error: error.message
      }
    }

    // Test 3: Send endpoint availability (without actually sending)
    try {
      const sendEndpoint = zohoConfig.sendTemplateEndpoint()
      // Just test if endpoint is reachable with HEAD request
      await axios.head(sendEndpoint, { timeout: 5000 })
      diagnostics.tests.sendEndpoint = {
        passed: true,
        message: 'Endpoint accessible'
      }
    } catch (error) {
      // A 405 (Method Not Allowed) is actually good - means endpoint exists
      diagnostics.tests.sendEndpoint = {
        passed: error.response?.status === 405,
        error: error.response?.status === 405 ? 'Endpoint accessible' : error.message
      }
    }

    return diagnostics
  }

  /**
   * Get service statistics and metrics
   */
  function getServiceMetrics() {
    return {
      connectionStatus: getConnectionStatus(),
      statistics: {
        totalRequests: state.successCount + state.errorCount,
        successfulRequests: state.successCount,
        failedRequests: state.errorCount,
        successRate: state.successCount + state.errorCount > 0
          ? Math.round((state.successCount / (state.successCount + state.errorCount)) * 100)
          : 0,
        activeRequests: state.activeRequests,
        queueLength: state.queue.length
      },
      health: {
        isHealthy: state.isHealthy,
        lastHealthCheck: state.lastHealthCheck,
        responseTime: state.responseTime
      },
      configuration: {
        environment: zohoConfig.getEnvironmentName(),
        baseUrl: zohoConfig.getZohoMiddlewareUrl(),
        maxRetries: config.maxRetries,
        requestTimeout: config.requestTimeout,
        healthCheckInterval: config.healthCheckInterval
      }
    }
  }

  /**
   * Reset service statistics
   */
  function resetMetrics() {
    state.errorCount = 0
    state.successCount = 0
    state.queue = []
  }

  // Initialize service
  function initialize() {
    startHealthMonitoring()

    // Set up cleanup on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        stopHealthMonitoring()
      })
    }
  }

  /**
   * Fetch document PDF as a Blob from the middleware
   * @param {string} documentId - The Zoho Sign document/request ID
   * @returns {Promise<Blob>} PDF blob
   */
  async function fetchDocumentPdf(documentId) {
    if (!documentId) {
      throw new Error('Document ID is required')
    }

    state.activeRequests += 1

    try {
      const pdfEndpoint = zohoConfig.documentPdfEndpoint(documentId)

      const response = await axios.get(pdfEndpoint, {
        responseType: 'blob',
        timeout: 30000,
        headers: {
          Accept: 'application/pdf',
        },
      })

      // Verify we got a PDF back (not a JSON error)
      if (response.data.type && response.data.type.includes('application/json')) {
        const text = await response.data.text()
        const errorData = JSON.parse(text)
        throw new Error(errorData.message || 'Failed to fetch PDF')
      }

      state.successCount += 1
      return response.data
    } catch (error) {
      state.errorCount += 1
      const errorInfo = handleApiError(error, 'Zoho Document PDF')
      throw new Error(errorInfo.friendlyMessage?.message || 'Failed to fetch document PDF')
    } finally {
      state.activeRequests -= 1
    }
  }

  // Auto-initialize
  initialize()

  // Export public API
  return {
    // Core functionality
    sendSingleContract,
    sendMultipleContracts,
    fetchDocumentPdf,

    // Health monitoring
    performHealthCheck,
    startHealthMonitoring,
    stopHealthMonitoring,
    getConnectionStatus,

    // Diagnostics
    testConnectivity,
    getServiceMetrics,
    resetMetrics,

    // Utilities
    createCancellationToken,

    // State access (read-only)
    get isHealthy() { return state.isHealthy },
    get responseTime() { return state.responseTime },
    get activeRequests() { return state.activeRequests }
  }
}
